import { useCallback, useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  itemForSend,
  type ChangeRequestCategoryTemplate,
  type DraftSidecar,
  type StorageAdapter,
  type StoredFacility,
  type StoredKit,
  type StoredUtilityRequirements,
  type SyncEngine,
} from "@njbsoft/field-sync";
import { type DeviceRecordBase } from "@njbsoft/device-input/core";
import { uuidv7 } from "@njbsoft/utils";
import { EmptyState, PageSkeleton } from "@/components/ui";
import { DraftLockedElsewhere } from "@/components/draft-locked-elsewhere";
import { enqueueSet } from "@/components/tester/change-requests/staging/enqueue-set";
import {
  type ReplaceRequest,
  type ReplaceStep,
} from "@/components/tester/change-requests/staging/replace-model";
import { ReplaceFlow } from "@/components/tester/change-requests/staging/replace-steps";
import {
  type KeptTest,
  keptTests,
  resubmittedItem,
} from "@/components/tester/change-requests/staging/resubmit";
import {
  type DraftSidecarHandle,
  useDraftSidecar,
} from "@/components/tester/change-requests/staging/use-draft-sidecar";
import { useFieldworkStorage } from "@/lib/fieldwork/storage-provider";
import { useDraftLock } from "@/hooks/use-draft-lock";

type StandaloneSidecar = Extract<DraftSidecar, { kind: "draft_set"; anchor: "standalone" }>;

interface StandaloneReplaceSearch {
  draft?: string;
  step?: ReplaceStep;
}

const STEPS: readonly ReplaceStep[] = ["assembly", "test", "review"];

export const Route = createFileRoute("/_company/my-work/replace/$installationId")({
  validateSearch: (search: Record<string, unknown>): StandaloneReplaceSearch => ({
    draft: typeof search.draft === "string" && search.draft !== "" ? search.draft : undefined,
    step: STEPS.find((step) => step === search.step),
  }),
  component: StandaloneReplacePage,
});

/**
 * Replace device from the facility screen: no assignment and no test of the
 * old assembly, so it was replaced today before any test or before this
 * visit, and the tester says which. The draft set's sidecar
 * — handed over with the locked facility and the predecessor's record — is
 * the whole source; the page takes the draft's lock and reads nothing else it
 * cannot do without.
 */
function StandaloneReplacePage() {
  const { t } = useTranslation("change-requests");
  const { draft } = Route.useSearch();
  if (draft === undefined) {
    return (
      <div className="p-4 sm:p-8">
        <EmptyState title={t("replace.title")} description={t("replace.draftMissing")} />
      </div>
    );
  }
  return <StandaloneReplaceLocked clientSetId={draft} />;
}

function StandaloneReplaceLocked({ clientSetId }: { clientSetId: string }) {
  const draftLock = useDraftLock(clientSetId);
  if (draftLock === "heldElsewhere") return <DraftLockedElsewhere />;
  if (draftLock === "acquiring") {
    return (
      <div className="p-4 sm:p-8">
        <PageSkeleton />
      </div>
    );
  }
  return <StandaloneReplaceDraft clientSetId={clientSetId} />;
}

interface LocalReads {
  facility: StoredFacility | null;
  requirements: StoredUtilityRequirements | null;
  kits: StoredKit[];
  template: ChangeRequestCategoryTemplate | null;
  /** The set is queued: the draft is no longer editable, and stays until it is received. */
  sent: boolean;
  /** A resubmission's rejected test, when the rejection is still on this device. */
  kept: KeptTest | null;
}

function StandaloneReplaceDraft({ clientSetId }: { clientSetId: string }) {
  const { t } = useTranslation("change-requests");
  const { storage, engine } = useFieldworkStorage();
  const handle = useDraftSidecar<"draft_set">(clientSetId);
  // A standalone draft without a record base is a new installation's, not a replacement's.
  const sidecar = handle.sidecar?.anchor === "standalone" ? handle.sidecar : null;
  const recordBase = sidecar?.recordBase ?? null;
  const orgId = sidecar?.orgId ?? null;
  const facilityId = sidecar?.facility.id ?? null;
  const resubmitted = sidecar?.resubmission?.requestId ?? null;
  const [reads, setReads] = useState<LocalReads | null>(null);

  useEffect(() => {
    if (storage === null || orgId === null || facilityId === null) return;
    let stale = false;
    void (async () => {
      const [facility, requirements, kits, template, rows, sets] = await Promise.all([
        storage.getBundleFacility(facilityId),
        storage.getUtilityRequirements(orgId),
        storage.listBundleKits(),
        storage.getCategoryTemplate(orgId, "testable_assembly"),
        storage.listChangeRequestSetRows(),
        resubmitted === null ? [] : storage.listBundleChangeRequestSets(),
      ]);
      if (stale) return;
      setReads({
        facility,
        requirements,
        kits,
        template,
        sent: rows.some((row) => row.clientSetId === clientSetId),
        kept: resubmitted === null ? null : (keptTests(sets).get(resubmitted) ?? null),
      });
    })().catch((err: unknown) => {
      // A read the unmount outran settles against a store this page no longer
      // reads from; only a mounted page has a failure to report.
      if (!stale) console.error("replacement draft reads failed", err);
    });
    return () => {
      stale = true;
    };
  }, [storage, orgId, facilityId, clientSetId, resubmitted]);

  if (handle.status === "error") {
    return (
      <p role="alert" className="p-4 text-sm text-danger sm:p-8">
        {t("replace.loadFailed")}
      </p>
    );
  }
  if (handle.status === "ready" && (sidecar === null || recordBase === null)) {
    return (
      <div className="p-4 sm:p-8">
        <EmptyState title={t("replace.title")} description={t("replace.draftMissing")} />
      </div>
    );
  }
  if (
    sidecar === null ||
    recordBase === null ||
    reads === null ||
    storage === null ||
    engine === null
  ) {
    return (
      <div className="p-4 sm:p-8">
        <PageSkeleton />
      </div>
    );
  }
  if (reads.sent) {
    return (
      <div className="p-4 sm:p-8">
        <EmptyState
          title={t("replace.title")}
          description={t("replace.sent", { utility: sidecar.facility.orgName })}
        />
      </div>
    );
  }
  return (
    <StandaloneReplaceSession
      storage={storage}
      engine={engine}
      clientSetId={clientSetId}
      sidecar={sidecar}
      recordBase={recordBase}
      reads={reads}
      update={handle.update}
    />
  );
}

function StandaloneReplaceSession({
  storage,
  engine,
  clientSetId,
  sidecar,
  recordBase,
  reads,
  update,
}: {
  storage: StorageAdapter;
  engine: SyncEngine;
  clientSetId: string;
  sidecar: StandaloneSidecar;
  recordBase: DeviceRecordBase;
  reads: LocalReads;
  update: DraftSidecarHandle<"draft_set">["update"];
}) {
  const { t } = useTranslation("change-requests");
  const navigate = useNavigate();
  const { installationId } = Route.useParams();
  const { step = "assembly" } = Route.useSearch();
  const item = sidecar.items[0];
  const stored: ReplaceRequest | null = item === undefined ? null : item;
  const [requestId] = useState(() => item?.item.client_request_id ?? uuidv7());
  const { facility, orgId, resubmission } = sidecar;
  const utilityName = facility.orgName;

  const writeRequest = useCallback(
    (change: (current: ReplaceRequest | null) => ReplaceRequest) =>
      update((current) => {
        if (current?.anchor !== "standalone") throw new Error("standalone_draft_missing");
        return { ...current, items: [change(current.items[0] ?? null)] };
      }),
    [update],
  );

  // The sidecar stays after the send: the received set's cleanup finds the
  // staged tests' photo owners through it, then deletes it.
  const finish = useCallback(
    async (request: ReplaceRequest) => {
      await enqueueSet(storage, engine, {
        now: new Date(),
        orgId,
        anchor: "standalone",
        clientSetId,
        facilityId: facility.id,
        items: [
          resubmission === null
            ? itemForSend(request)
            : resubmittedItem(resubmission.requestId, request.item),
        ],
      });
      void navigate({ to: "/claim/facility/$facilityId", params: { facilityId: facility.id } });
    },
    [storage, engine, orgId, clientSetId, facility.id, resubmission, navigate],
  );

  const toFacility = () => {
    void navigate({ to: "/claim/facility/$facilityId", params: { facilityId: facility.id } });
  };

  return (
    <ReplaceFlow
      anchor="standalone"
      step={step}
      onStep={(next) => {
        update((current) => ({ ...(current ?? sidecar), step: next })).catch(() => undefined);
        void navigate({
          to: "/my-work/replace/$installationId",
          params: { installationId },
          search: { draft: clientSetId, step: next },
        });
      }}
      storage={storage}
      orgId={orgId}
      utilityName={utilityName}
      facility={facility}
      bundleFacility={reads.facility}
      predecessor={recordBase}
      predecessorInstallationId={installationId}
      owner={clientSetId}
      requestId={requestId}
      stored={stored}
      template={reads.template}
      requirements={reads.requirements}
      kits={reads.kits}
      oldTest={null}
      reuse={null}
      resubmission={resubmission === null ? null : { pin: resubmission, kept: reads.kept }}
      initialKitId={null}
      kitNote={t("replace.test.kitNote")}
      backTo={`/claim/facility/${facility.id}`}
      backLabel={t("replace.fromFacility")}
      writeRequest={writeRequest}
      finish={finish}
      finishLabel={t(
        resubmission === null ? "submit.submitReplacement" : "resubmit.submit.replacement",
      )}
      onCancel={toFacility}
    />
  );
}
